import type { PartnerCounts } from '../hooks/usePartnerCounts';

/**
 * „● N offen" — offene Partner-Items (Inbox / Feedback) als Chip in der Kopfleiste.
 *
 * Geschichte: bis 03.10.2026 ein eigener Streifen ganz oben ueber der Leiste
 * („Globaler, immer sichtbarer Streifen … egal welches Panel gerade aktiv ist").
 * Er loeste "geht unter / versteckt im Tool Hub". Rafael hat am 03.10.2026 10:15
 * zugestimmt, ihn als Chip in die Kopfleiste zu holen — die Leiste ist ebenso global
 * und ebenso unabhaengig vom aktiven Panel, kostet aber keine eigene Zeile. Farbe,
 * Zaehlung (`usePartnerCounts`) und Klickziel sind dieselben geblieben.
 *
 * Anklickbar (Sahori, 24.09.2026): der Streifen zeigte „2 Inbox", liess sich aber
 * nicht anklicken — ihren Posteingang fand sie nur ueber „+ → Nachrichten". Der
 * Klick oeffnet genau dieses Panel (bei nur Feedback das Feedback-Panel).
 */
export type PartnerOffenZiel = 'partner-inbox' | 'feedback' | 'group-chat';

export function partnerOffenText(c: PartnerCounts): { kurz: string; aufschluesselung: string; ziel: PartnerOffenZiel } {
  const teile: string[] = [];
  if (c.inbox > 0) teile.push(`${c.inbox} Inbox`);
  if (c.feedback > 0) teile.push(`${c.feedback} Feedback`);
  return { kurz: `${c.total} offen`, aufschluesselung: teile.join(' · '), ziel: c.inbox > 0 ? 'partner-inbox' : 'feedback' };
}

export const PARTNER_OFFEN_FARBE = 'var(--tn-red)';

/** Der Chip in der aufgeklappten Leiste. Nichts offen -> nichts da. */
export function PartnerOffenChip({ counts, onOpen }: { counts: PartnerCounts; onOpen?: (component: PartnerOffenZiel) => void }) {
  if (counts.total === 0) return null;
  const t = partnerOffenText(counts);
  const zielText = t.ziel === 'partner-inbox' ? 'Posteingang öffnen' : 'Feedback öffnen';
  return (
    <button
      type="button"
      data-ai-id="kopf-offen-chip"
      className="cui-kopf-offen-chip"
      onClick={onOpen ? () => onOpen(t.ziel) : undefined}
      disabled={!onOpen}
      title={`${t.kurz}: ${t.aufschluesselung}${onOpen ? ` — Klick: ${zielText}` : ''}`}
      aria-label={`${t.kurz}: ${t.aufschluesselung}${onOpen ? `. ${zielText}` : ''}`}
    >
      <span aria-hidden="true" style={{ fontSize: 9 }}>●</span> {t.kurz}
    </button>
  );
}

/**
 * DER GRUPPENRAUM VORN IN DER KOPFLEISTE (Rafael, Tagesauftrag 02.11.2026 Punkt 5: „Gemeinsamer Partner-Chatroom
 * prominent vorne beim Kalender/Arbeitsbereich, sichtbares rotes Signal bei neuen Nachrichten"). Immer da fuer jeden,
 * der den Raum sehen darf — nicht erst, wenn etwas offen ist: der Raum soll auffindbar sein, nicht nur das Signal.
 * Die rote Zahl ist derselbe Stil wie „● N offen" (PARTNER_OFFEN_FARBE). Klick oeffnet das vorhandene GroupChatPanel.
 */
export function GruppenraumKnopf({ ungelesen, onOpen }: { ungelesen: number; onOpen?: (component: PartnerOffenZiel) => void }) {
  const text = ungelesen > 0 ? `Gruppenraum: ${ungelesen} neue Nachricht${ungelesen === 1 ? '' : 'en'}` : 'Gruppenraum: keine neuen Nachrichten';
  return (
    <button
      type="button"
      data-ai-id="kopf-gruppenraum"
      className="cui-kopf-gruppen-chip"
      data-neu={ungelesen > 0 ? '' : undefined}
      onClick={onOpen ? () => onOpen('group-chat') : undefined}
      disabled={!onOpen}
      title={`${text}${onOpen ? ' — Klick: Gruppenraum öffnen' : ''}`}
      aria-label={`${text}${onOpen ? '. Gruppenraum öffnen' : ''}`}
    >
      <span aria-hidden="true">💬</span> Gruppenraum
      {ungelesen > 0 && <span className="cui-kopf-gruppen-zahl" aria-hidden="true">{ungelesen > 99 ? '99+' : ungelesen}</span>}
    </button>
  );
}
